import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Popper, ClickAwayListener, MenuList, MenuItem } from '@material-ui/core';
import classNames from 'classnames';
import _ from 'lodash';

import './StakeOptions.scss';
import { STAKE_SELECTIONS, SLIPS } from 'src/utils/content';
import { RootState, Dispatch, select } from 'src/configs/store';
import StakeInput from 'src/components/StakeInput/StakeInput';
import Icon from 'src/components/Icon/Icon';
import useIsMobile from 'src/hooks/useIsMobile';

interface StakeOptionsProps {
  valid: boolean
}

export default function StakeOptions({ valid }: StakeOptionsProps) {
  const { t } = useTranslation('common');
  const mobile = useIsMobile();

  const [open, setOpen] = useState(false);
  const stakeAnchor = useRef(null);

  const slip =  useSelector((state: RootState) => state.betslip.slip);
  const singleSlip =  useSelector((state: RootState) => state.betslip.singleSlip);
  const accaSlip =  useSelector((state: RootState) => state.betslip.accaSlip);
  const systemSlip =  useSelector((state: RootState) => state.betslip.systemSlip);
  const divisor =  useSelector((state: RootState) => state.betslip.divisor);
  const bets =  useSelector((state: RootState) => state.betslip.bets);

  const totalCombinationsGreater = useSelector((state: RootState) => {
    const combo = state.betslip.stakes.length > 0 ? _.reduce(state.stakes, (total, s) => total += s.betsCount, 0) : 0;
    return combo > 1;
  }
  );

  const noCombinableBets = useSelector(select.betslip.noCombinableBets);
  const dispatch = useDispatch<Dispatch>();

  const onStakeSelectionChange = (value) => (e) => {
    e.preventDefault();

    setOpen(false);
    dispatch.betslip.changeStakeSelection(value);
  };

  const onAmountChange = (name: string, value: string) => {
    dispatch.betslip.updateBetStake({ value: +value });
  };

  useEffect(() => {
    if (!totalCombinationsGreater && slip === 1 && accaSlip.stakeSelection !== 'totalStake'){
      dispatch.betslip.changeStakeSelection('totalStake');
    }
  }, [totalCombinationsGreater, slip]);

  const getStakeNaming = (key) => {
    if (key === 'sameStake' && slip === 1){
      return 'sameStakeAcca';
    } else if (key === 'differentStake' && slip === 2){
      return 'differentStakePerCombo';
    }
    return key;
  };

  const renderStakeList = useCallback(() => {
    const hasComboBet = _.findIndex(bets, bet => bet.permutationsCount > 1) >= 0;

    let selectedSlip = singleSlip;
    if (slip === 1) selectedSlip = accaSlip;
    else if (slip === 2) selectedSlip = systemSlip;

    return (
      <MenuList
        className="stake-options__stakes-options"
        autoFocusItem={ open }
      >
        {
          _.map(STAKE_SELECTIONS, (selection, key) => {
            let render = false;

            if (key === 'sameStake' && slip === 1) {
              if (
                selection.indexOf(slip) !== -1 &&
                slip === 1 &&
                !noCombinableBets &&
                totalCombinationsGreater
              ) {
                render = true;
              }
            } else if (selection.indexOf(slip) !== -1) {
              render = true;
            }

            return render && selectedSlip.stakeSelection !== key && (
              <MenuItem
                key={ key }
                className="stake-options__stake-option"
                disabled={ key === 'totalStake' && hasComboBet }
                onClick={ onStakeSelectionChange(key) }
                selected={ selectedSlip.stakeSelection === key }
              >
                { t(`betslip.${ getStakeNaming(key) }`) }
              </MenuItem>
            );
          })
        }
      </MenuList>
    );
  }, [bets, slip, singleSlip, accaSlip, systemSlip]);

  const amount = useMemo(() => {
    let _amount = '0';

    switch (slip) {
      case 0:
        _amount = (singleSlip[singleSlip.stakeSelection] / divisor).toString();
        break;

      case 1:
        _amount = (accaSlip[accaSlip.stakeSelection] / divisor).toString();
        break;

      case 2:
        _amount = (systemSlip[systemSlip.stakeSelection] / divisor).toString();
        break;
    }

    return _amount;
  }, [singleSlip, accaSlip, systemSlip, slip]);


  let selectedSlip = singleSlip;
  if (slip === 1) selectedSlip = accaSlip;
  else if (slip === 2) selectedSlip = systemSlip;

  return (
    <>
      <div className="stake-options">
        <span
          ref={ stakeAnchor }
          className="stake-options__stake-dropdown"
          onClick={ () => setOpen(!open) }
        >
          { t(`betslip.${ getStakeNaming(selectedSlip.stakeSelection) }`) }
        </span>
        {
          !mobile && (
            <Popper
              className="stake-options__options-container"
              open={ open }
              anchorEl={ stakeAnchor.current }
              disablePortal
              placement="bottom-start"
              modifiers={
                {
                  offset: {
                    enabled: true,
                    offset: '-10, 14'
                  },
                  flip: {
                    enabled: true,
                    padding: 6
                  }
                }
              }
            >
              <ClickAwayListener onClickAway={ () => setOpen(false) }>
                { renderStakeList() }
              </ClickAwayListener>
            </Popper>
          )
        }

        {
          selectedSlip.stakeSelection !== 'differentStake' && selectedSlip.stakeSelection !== 'stakePerSystem' && (
            <>
              { !valid && <Icon icon="warningFilled" className="stake-options__warning-icon" /> }
              <StakeInput
                rule={ `sports.${ SLIPS[slip] }` }
                type="betslip"
                onChange={ onAmountChange }
                defaultValue={ amount }
                name="amount"
                className="stake-options__stake-input"
              />
            </>
          )
        }

        <Icon
          icon="arrow"
          className={ classNames('stake-options__stake-dropdown-arrow', { 'stake-options__stake-dropdown-arrow--opened': open }) }
          onClick={ () => setOpen(!open) }
        />
      </div>

      {
        open && mobile && (
          <div className="">
            { renderStakeList() }
          </div>
        )
      }
    </>
  );
}
